import colorVars from "@/assets/styles/colorVars"
import MobileBackgroundSrc from "@/assets/svg/animation/mobile-background.svg"
import PauseBtnSrc from "@/assets/svg/animation/pause-btn.inline.svg"
import PhoneSrc from "@/assets/svg/animation/phone.svg"
import PlayBtnSrc from "@/assets/svg/animation/play-btn.inline.svg"
import WaveformEmptySrc from "@/assets/svg/animation/waveform-empty.svg"
import WaveformFullSrc from "@/assets/svg/animation/waveform-full.svg"
import anime, { AnimeTimelineInstance } from "animejs"
import React, { FC, useEffect, useRef, useState } from "react"
import styled from "styled-components"

import { ISequenceTiming, Omnichannel } from "./AnimationPlayer"
import { handleAvatarImage } from "./animationHelpers"

export interface IAudioScene {
  omnichannel: Omnichannel
  sequenceTiming: ISequenceTiming
  voiceMessage?: {
    avatar: string
    duration: number
    message: string
    phone: string
  }
}

// Audio base components
const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0;

  /* Floating phone-thing */
  &::before {
    position: absolute;
    top: 13%;
    left: 40%;
    display: block;
    width: 100%;
    max-width: 237px;
    height: 100%;
    max-height: 478px;
    content: "";
    background: url("${MobileBackgroundSrc}");
  }

  /* Gradient circle falloff effect */
  &::after {
    position: absolute;
    top: calc(50% - 320px);
    left: calc(50% - 280px);
    display: block;
    width: 100%;
    max-width: 700px;
    height: 100%;
    max-height: 700px;
    content: "";
    background: radial-gradient(
      rgba(173, 159, 255, 0.22) 0%,
      rgba(173, 159, 255, 0) 60%,
      rgba(173, 159, 255, 0) 100%
    );
  }
`

const AudioPlayer = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  opacity: 0;
`
const Avatar = styled.img`
  position: absolute;
  top: -30px;
  left: 16px;
  border: 2px solid ${colorVars.violet.main};
  border-radius: 50px;
  box-shadow: 0 4px 16px rgba(0, 24, 69, 0.16);
`

// Audio player
const AudioMessage = styled.div`
  display: flex;
  gap: 0 12px;
  align-items: center;
  justify-content: center;
  height: 92px;
  background: #2f3147;
  border-radius: 8px;
`
const Timeline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const WaveformEmpty = styled.div`
  display: block;
  width: 166px;
  height: 50px;
  background: url(${WaveformEmptySrc}) no-repeat center center;
`
const WaveformFull = styled.div`
  display: block;

  /* Animate this to make the playback illusion work */
  width: 50%;
  height: 100%;
  content: "";
  background: url(${WaveformFullSrc});
`
const SeekController = styled.div``
const Time = styled.div`
  min-width: 48px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  text-align: center;
  user-select: none;

  &::before {
    content: "00:";
  }
`
const PlaybackControlBtn = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
`
const PlayBtn = styled(PlayBtnSrc)`
  position: absolute;
`
const PauseBtn = styled(PauseBtnSrc)`
  position: absolute;
`

// Message transcript
const MessageTranscript = styled.div`
  position: relative;
  width: 100%;
  max-width: 290px;
  margin: 16px 0 0 auto;
  background: #0b0b1b;
  border-radius: 8px;
  opacity: 0;
  transform-origin: top left;

  /* Triangle thingy (source: https://css-tricks.com/snippets/css/css-triangle/) */
  &::before {
    position: absolute;
    top: -30px;
    left: 0;
    display: block;
    width: 0;
    height: 0;
    content: "";
    border-top: 30px solid transparent;
    border-bottom: 30px solid transparent;
    border-left: 30px solid #0b0b1b;
  }
`
const MessageText = styled.div`
  padding: 16px 16px 0;
  font-size: 14px;
  line-height: 22px;
  color: white;
  user-select: none;
`
const PhoneNumber = styled.div`
  position: relative;
  top: 15px;
  left: 16px;
  display: inline-flex;
  gap: 0 12px;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
  line-height: 22px;
  color: white;
  user-select: none;

  background: linear-gradient(
      104deg,
      rgba(0, 0, 0, 0.42) 0%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(0deg, #7c6be0 0%, #7c6be0 100%), #fff;
  border-radius: 4px;

  &::before {
    display: inline-block;
    width: 12px;
    height: 12px;
    content: "";
    background: url("${PhoneSrc}");
  }
`

export const Audio: FC<IAudioScene> = ({ sequenceTiming, voiceMessage }) => {
  if (!voiceMessage) return null

  // Refs
  const timelineRef = useRef<AnimeTimelineInstance>()
  const AudioPlayerRef = useRef<HTMLDivElement>(null)
  const BackgroundElementsRef = useRef<HTMLDivElement>(null)
  const WaveformEmptyRef = useRef<HTMLDivElement>(null)
  const PlaybackControlBtnRef = useRef<HTMLDivElement>(null)
  const TimeRef = useRef<HTMLDivElement>(null)
  const MessageTranscriptRef = useRef<HTMLDivElement>(null)
  const MessageTextRef = useRef<HTMLDivElement>(null)

  // State
  const [buttonVisualPlayback, setButtonVisualPlayback] = useState(false)

  // Effect handling
  useEffect(() => {
    // Handle Anime.js timeline
    timelineRef.current = anime.timeline({
      autoplay: true,
      easing: "easeOutCubic",
      loop: false,
    })

    // Transform message text into single characters
    if (MessageTextRef.current && voiceMessage) {
      // Spread out string to an array
      const chars = [...voiceMessage.message]
      const formattedMessage = chars
        .map((char) => {
          return `<span>${char}</span>`
        })
        .join("")

      MessageTextRef.current.innerHTML = formattedMessage
    }

    // Animate items via Timeline
    if (timelineRef.current && voiceMessage) {
      timelineRef.current
        // Reveal player
        .add({
          duration: 1000,
          opacity: [0, 1],
          scale: [0.5, 1],
          targets: AudioPlayerRef.current,
          translateY: [90, 90],
        })
        // Fade in background
        .add(
          {
            duration: 1250,
            opacity: [0, 1],
            scale: [0.4, 1],
            targets: BackgroundElementsRef.current,
            translateY: [150, 0],
          },
          "-=850",
        )
        // Play button pulse
        .add({
          complete: () => setButtonVisualPlayback(true),
          duration: 600,
          scale: [1, 0.9, 1],
          targets: PlaybackControlBtnRef.current,
        })
        // Player slides now
        .add({
          duration: 800,
          targets: AudioPlayerRef.current,
          translateY: [90, 0],
        })
        // Start playback
        .add(
          {
            duration: sequenceTiming.audioMessagePlaybackDuration,
            easing: "linear",
            targets: WaveformEmptyRef.current?.children[0],
            width: ["0%", "100%"],
          },
          "-=600",
        )
        // Reveal transcript
        .add(
          {
            duration: 600,
            opacity: [0, 1],
            scale: [0.6, 1],
            targets: MessageTranscriptRef.current,
            translateY: [50, 0],
          },
          `-=${sequenceTiming.audioMessagePlaybackDuration}`,
        )
        // Increment timer
        .add(
          {
            duration: sequenceTiming.audioMessagePlaybackDuration,
            easing: "linear",
            innerHTML: ["00", voiceMessage.duration],
            round: 1,
            targets: TimeRef.current,
            update: (anim) => {
              if (TimeRef.current) {
                const value: any = anim.animations[0].currentValue
                TimeRef.current.innerHTML =
                  value < 10 ? `0${Math.round(value)}` : value
              }
            },
          },
          `-=${sequenceTiming.audioMessagePlaybackDuration}`,
        )
        // Reveal message text
        .add(
          {
            delay: anime.stagger(
              MessageTextRef.current
                ? sequenceTiming.audioMessagePlaybackDuration /
                    MessageTextRef.current.children.length
                : 50,
            ), // Delay each character's animation by 50ms
            duration: 10, // Duration for each character's animation
            easing: "linear",
            opacity: [0, 1], // Animate each span's opacity to 1
            targets: MessageTextRef.current?.children,
          },
          `-=${sequenceTiming.audioMessagePlaybackDuration}`,
        )
        // Play button pulse
        .add({
          // Begin next scene as this transition happens
          complete: () => setButtonVisualPlayback(false),
          duration: 600,
          scale: [1, 0.9, 1],
          targets: PlaybackControlBtnRef.current,
        })
        // Fade out background
        .add({
          delay: sequenceTiming.timeBetweenScenes,
          duration: 450,
          opacity: [1, 0],
          scale: [1, 0.4],
          targets: BackgroundElementsRef.current,
        })
        // Hide player
        .add(
          {
            begin: () => {
              window.dispatchEvent(new Event("pxmPreTransitionSceneComplete"))
            },
            duration: 550,
            opacity: [1, 0],
            scale: [1, 0.8],
            targets: AudioPlayerRef.current,
          },
          "-=500",
        )
    }

    // Initialize timer at 00
    if (TimeRef.current) TimeRef.current.innerHTML = "00"

    // Cleanup
    return () => {
      timelineRef.current?.pause()
      timelineRef.current = undefined
    }
  }, [])

  return (
    <>
      <BackgroundElements ref={BackgroundElementsRef} />
      <AudioPlayer ref={AudioPlayerRef}>
        <Avatar
          height="50"
          src={handleAvatarImage(voiceMessage.avatar)}
          width="50"
        />
        <AudioMessage>
          <Timeline>
            <WaveformEmpty ref={WaveformEmptyRef}>
              <WaveformFull />
            </WaveformEmpty>
            <SeekController />
          </Timeline>
          <Time ref={TimeRef} />
          <PlaybackControlBtn ref={PlaybackControlBtnRef}>
            {buttonVisualPlayback ? <PauseBtn /> : <PlayBtn />}
          </PlaybackControlBtn>
        </AudioMessage>
        <MessageTranscript ref={MessageTranscriptRef}>
          <MessageText ref={MessageTextRef}>{voiceMessage.message}</MessageText>
          <PhoneNumber>{voiceMessage.phone}</PhoneNumber>
        </MessageTranscript>
      </AudioPlayer>
    </>
  )
}
